import { deletePersonalKeys, saveSSOKeys } from '@faceup/crypto'
import { createLocalStorage } from '@faceup/ui'
import { type InferType, yup } from '@faceup/validation'
import { createContext, type ReactNode } from 'react'
import { useAuth } from '../utils/auth'

const storageKey = 'savedReports'
const contentMaxLength = 64

export type SavedReport = InferType<typeof schema>[number]

type SavedReportsContextProps = {
  reports: SavedReport[]
  addReport: (report: SavedReport) => void
  removeReport: (report: SavedReport) => void
  clearReports: () => void
  loginIntoReport: (report: SavedReport) => void
  hasSomeSavedReports: boolean
}

// No default value: clearReports drops case credentials on app reset, and a no-op default would
// make that fail silently if the provider ever moved out from above AppLockProvider.
export const SavedReportsContext = createContext<SavedReportsContextProps | null>(null)

const schema = yup
  .array()
  .of(
    yup.object().shape({
      keys: yup.object().shape({
        jwt: yup.string().required(),
        publicKey: yup.string().required(),
        privateKey: yup.string().required(),
      }),
      id: yup.string().required(),
      createdAt: yup.string().required(),
      category: yup.string().required(),
      content: yup.string().required(),
      status: yup.object().shape({
        color: yup.string().required(),
        name: yup.string().required(),
      }),
    })
  )
  .required()

const useLocalStorage = createLocalStorage(storageKey, schema, [])

type SavedReportsProviderProps = {
  children: ReactNode
}

export const SavedReportsProvider = (props: SavedReportsProviderProps) => {
  const { children } = props
  const [reports, setReports] = useLocalStorage()
  const { setJwt } = useAuth()

  const addReport = (report: SavedReport) => {
    setReports([
      ...reports.filter(({ id }) => id !== report.id),
      {
        ...report,
        content:
          report.content.length > contentMaxLength
            ? `${report.content.slice(0, contentMaxLength)}…`
            : report.content,
      },
    ])
  }

  const removeReport = (report: SavedReport) => {
    setReports([...reports.filter(({ id }) => id !== report.id)])
  }

  const clearReports = () => setReports([])

  const loginIntoReport = async (report: SavedReport) => {
    await setJwt(report.keys.jwt)
    deletePersonalKeys()
    await saveSSOKeys({
      publicKey: report.keys.publicKey,
      privateKey: report.keys.privateKey,
      rememberMe: true,
    })
  }

  const hasSomeSavedReports = reports.length > 0

  return (
    <SavedReportsContext.Provider
      value={{
        reports,
        addReport,
        removeReport,
        clearReports,
        loginIntoReport,
        hasSomeSavedReports,
      }}
    >
      {children}
    </SavedReportsContext.Provider>
  )
}
